import { Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { FlexibleConnectedPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { Observable, of, ReplaySubject } from 'rxjs';

export interface VirtualKeyboardOptions {
  // For tablet - append message line height
  extraHeight?: number;

  // For zp.midpass.ru - fix scrollIntoView
  insideScroll?: boolean;
}

export abstract class VirtualKeyboard {
  abstract layout(): void;
  abstract shift(): void;
  abstract caps(): void;
  abstract key(): void;
  abstract show(keyboardLayoutLanguage: 'ru' | 'en' | null): void;
  abstract hide(): void;

  abstract getNavigationDir(): boolean;
  abstract getFocusDisabled(): boolean;
  abstract getBlurDisabled(): boolean;
}

export abstract class VirtualKeyboardService {
  abstract initialize(options: VirtualKeyboardOptions): void;
  abstract registerKeyboard(keyboard: VirtualKeyboard): void;
  abstract registerKey(): (key: string, long: boolean) => void;
  abstract getHeight(): number;
  abstract getInsideScroll(): boolean;
  abstract getVisible$(): Observable<boolean>;
  abstract center(ref: MatDialogRef<any>): void;
  abstract focus(
    element: HTMLInputElement | HTMLTextAreaElement,
    onChange: (val: string) => void,
    onNavigate: (dir: boolean) => void,
    moveIntoView: () => void,
    keyboardLayoutLanguage: 'ru' | 'en' | null,
  ): void;
  abstract blur(): void;
  abstract show(): void;
  abstract hide(): void;
}

@Injectable()
export class NullVirtualKeyboardService implements VirtualKeyboardService {
  initialize(options: VirtualKeyboardOptions): void {}
  registerKeyboard(keyboard: VirtualKeyboard): void {}
  registerKey(): (key: string, long: boolean) => void {
    return (key: string, long: boolean) => {};
  }
  getHeight(): number {
    return 0;
  }
  getInsideScroll(): boolean {
    return false;
  }
  getVisible$(): Observable<boolean> {
    return of(false);
  }
  center(ref: MatDialogRef<any>): void {}
  focus(
    element: HTMLInputElement | HTMLTextAreaElement,
    onChange: (val: string) => void,
    onNavigate: (dir: boolean) => void,
    moveIntoView: () => void,
    keyboardLayoutLanguage: 'ru' | 'en' | null,
  ): void {}
  blur(): void {}
  show(): void {}
  hide(): void {}
}

@Injectable()
export class RealVirtualKeyboardService implements VirtualKeyboardService {
  private options: VirtualKeyboardOptions;
  private keyboard: VirtualKeyboard;
  private element: HTMLInputElement | HTMLTextAreaElement | null = null;
  private moveIntoView: (() => void) | null = null;
  private onElementChange: ((val: string) => void) | null = null;
  private onElementNavigate: ((dir: boolean) => void) | null = null;
  private timer: any = null;
  private visible: boolean;
  private visibleSubj = new ReplaySubject<boolean>(1);
  private keyboardLayoutLanguage: 'ru' | 'en' | null = null;
  private dialogs: MatDialogRef<any>[] = [];

  constructor(private overlay: Overlay) {
    this.visible = false;
    this.visibleSubj.next(this.visible);
  }

  private repositionDialogs() {
    for (const dialog of this.dialogs) {
      /** второй вариант для mdc версии matDialog */
      const overlayRef = (dialog as any)._overlayRef
        ? ((dialog as any)._overlayRef as OverlayRef)
        : ((dialog as any)._ref.overlayRef as OverlayRef);
      const strategy = this.overlay
        .position()
        .global()
        .centerHorizontally()
        .centerVertically(this.visible ? '-' + this.getHeight() + 'px' : undefined);
      overlayRef.updatePositionStrategy(strategy);
    }
  }

  private clearTimer() {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private static computeBackspace(element: HTMLInputElement | HTMLTextAreaElement, long: boolean, len: number): number {
    if (long) {
      while (len && (element.value[len - 1] === ' ' || element.value[len - 1] === '\n')) {
        --len;
      }
      while (len && element.value[len - 1] !== ' ' && element.value[len - 1] !== '\n') {
        --len;
      }
      while (len && (element.value[len - 1] === ' ' || element.value[len - 1] === '\n')) {
        --len;
      }
    } else {
      --len;
    }
    return len;
  }

  private static updateInput(
    element: HTMLInputElement | HTMLTextAreaElement,
    onChange: ((val: string) => void) | null,
    key: string,
    long: boolean,
  ) {
    // selection
    const selectionStart = element.selectionStart;
    const selectionEnd = element.selectionEnd;
    if (selectionStart === null || selectionEnd === null) {
      return;
    }

    // compute text
    let beforeCaret;
    switch (key) {
      case 'backspace':
        if (selectionStart !== selectionEnd) {
          beforeCaret = element.value.substring(0, selectionStart);
        } else {
          const len = selectionStart;
          if (!len) {
            return;
          }
          beforeCaret = element.value.substring(0, RealVirtualKeyboardService.computeBackspace(element, long, len));
        }
        break;
      case 'space':
        beforeCaret = element.value.substring(0, selectionStart) + ' ';
        break;
      case 'enter':
        return;
      default:
        beforeCaret = element.value.substring(0, selectionStart) + key;
    }
    const afterCaret = element.value.substring(selectionEnd);

    // compute scroll
    let scroll: number | null = null;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (context !== null) {
      const style = getComputedStyle(element);
      context.font = style.fontSize + ' ' + style.fontFamily;
      const metrics = context.measureText(style.textTransform === 'uppercase' ? beforeCaret.toUpperCase() : beforeCaret);
      const width = metrics.width + 12; // compensate discrepancies (margins, paddings, borders, off by one errors, etc.)
      if (element.scrollLeft < width - element.clientWidth) {
        scroll = metrics.width - element.clientWidth / 2;
      }
    }

    // update element
    element.value = beforeCaret + afterCaret;
    element.setSelectionRange(beforeCaret.length, beforeCaret.length);

    // ensure scroll
    if (scroll !== null) {
      element.scrollLeft = scroll;
    }

    // notify
    if (onChange) {
      onChange(element.value);
    }
  }

  private static updateTextArea(
    element: HTMLInputElement | HTMLTextAreaElement,
    onChange: ((val: string) => void) | null,
    key: string,
    long: boolean,
  ) {
    // selection
    const selectionStart = element.selectionStart;
    const selectionEnd = element.selectionEnd;
    if (selectionStart === null || selectionEnd === null) {
      return;
    }

    // update element
    switch (key) {
      case 'backspace':
        if (selectionStart !== selectionEnd) {
          element.setRangeText('', selectionStart, selectionEnd, 'end');
        } else {
          const len = element.selectionStart;
          if (!len) {
            return;
          }
          element.setRangeText('', RealVirtualKeyboardService.computeBackspace(element, long, len), selectionEnd, 'end');
        }
        break;
      case 'space':
        element.setRangeText(' ', selectionStart, selectionEnd, 'end');
        break;
      case 'enter':
        element.setRangeText('\n', selectionStart, selectionEnd, 'end');
        break;
      default:
        element.setRangeText(key, selectionStart, selectionEnd, 'end');
    }

    // ensure scroll
    element.blur();
    element.focus();

    // notify
    if (onChange) {
      onChange(element.value);
    }
  }

  initialize(options: VirtualKeyboardOptions): void {
    this.options = options;
    this.fixFlexibleConnectedPositionStrategy();
  }

  registerKeyboard(keyboard: VirtualKeyboard): void {
    this.keyboard = keyboard;
  }

  registerKey(): (key: string, long: boolean) => void {
    return (key: string, long: boolean) => {
      switch (key) {
        case 'layout':
          this.keyboard.layout();
          break;
        case 'shift':
          this.keyboard.shift();
          break;
        case 'caps':
          this.keyboard.caps();
          break;
        case 'tab':
          if (this.onElementNavigate !== null) {
            this.onElementNavigate(this.keyboard.getNavigationDir());
          }
          this.keyboard.key();
          break;
        case 'hide':
          this.element?.blur();
          this.hide();
          break;
        default:
          if (this.element !== null) {
            switch (this.element.tagName.toLowerCase()) {
              case 'input':
                RealVirtualKeyboardService.updateInput(this.element, this.onElementChange, key, long);
                break;
              case 'textarea':
                RealVirtualKeyboardService.updateTextArea(this.element, this.onElementChange, key, long);
                break;
              default:
                console.log('Forbidden');
            }
          }
          this.keyboard.key();
      }
    };
  }

  getHeight(): number {
    return 342 + (this.options.extraHeight || 0);
  }

  getInsideScroll(): boolean {
    return this.options.insideScroll || false;
  }

  getVisible$(): Observable<boolean> {
    return this.visibleSubj;
  }

  private fixFlexibleConnectedPositionStrategy(): void {
    const proto = FlexibleConnectedPositionStrategy.prototype as any;
    if (!proto.__getNarrowedViewportRect) {
      proto.__getNarrowedViewportRect = proto._getNarrowedViewportRect;
      const self = this;
      proto._getNarrowedViewportRect = function () {
        const res = this.__getNarrowedViewportRect();
        if (self.visible) {
          res.bottom -= self.getHeight();
          res.height -= self.getHeight();
        }
        return res;
      };
    }
  }

  center(ref: MatDialogRef<any>): void {
    this.dialogs.push(ref);
    ref.afterClosed().subscribe((_) => {
      this.dialogs = this.dialogs.filter((x) => x !== ref);
    });
    this.repositionDialogs();
  }

  focus(
    element: HTMLInputElement | HTMLTextAreaElement,
    onChange: (val: string) => void,
    onNavigate: (dir: boolean) => void,
    moveIntoView: () => void,
    keyboardLayoutLanguage: 'ru' | 'en' | null = null,
  ) {
    this.element = element;
    this.moveIntoView = moveIntoView;
    this.onElementChange = onChange;
    this.onElementNavigate = onNavigate;
    if (this.keyboardLayoutLanguage !== keyboardLayoutLanguage) {
      this.keyboardLayoutLanguage = keyboardLayoutLanguage;
    }
    if (this.keyboard && !this.keyboard.getFocusDisabled()) {
      this.show();
    }
  }

  blur() {
    this.element = null;
    this.moveIntoView = null;
    this.onElementChange = null;
    this.onElementNavigate = null;
    if (this.keyboard && !this.keyboard.getBlurDisabled()) {
      this.clearTimer();
      this.timer = setTimeout(() => {
        if (!this.keyboard.getBlurDisabled()) {
          this.hide();
        }
      }, 301);
    }
  }

  show() {
    this.keyboard.show(this.keyboardLayoutLanguage);
    this.visible = true;
    this.visibleSubj.next(this.visible);
    this.repositionDialogs();
    this.clearTimer();
    this.timer = setTimeout(() => {
      if (!this.keyboard.getFocusDisabled()) {
        if (this.moveIntoView) {
          this.moveIntoView();
        }
      }
    }, 101);
  }

  hide() {
    this.keyboard.hide();
    this.visible = false;
    this.visibleSubj.next(this.visible);
    this.repositionDialogs();
  }
}
